import { useRef } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { TypeRegisterInfoState, useStockReleaseStore, useUserInfoStore } from '~/store';
import { addIf, focusFirstErrorField, validateEmailFormat, validateTelFormat } from '~/utils';

import {
  DEFAULT_INPUT_STATUS,
  ERROR_INVALID_FORMAT_INPUT_STATUS,
  ERROR_REQUIRED_INPUT_STATUS,
  ERROR_REQUIRED_SELECT_STATUS,
  ERROR_STATUS,
  STOCK_RELEASE_PACKING_METHOD_ALLOW_CODES,
  STOCK_RELEASE_PALLET_INFO_REQUIRED_CODES,
} from '~/constants';

import { TypeInputState } from '~/types/common.types';
import { TypeStockReleaseRegisterErrorFieldRefs } from '~/types/stock.types';

const DEFAULT_ERROR_FIELD_REFS: TypeStockReleaseRegisterErrorFieldRefs = {
  'detailInfo.method': null,
  'detailInfo.expectedDate': null,
  'detailInfo.packingMethod': null,
  'detailInfo.zipCode': null,
  'detailInfo.address': null,
  'detailInfo.detailAddress': null,
  'detailInfo.phoneNumber': null,
  'palletInfo.palletType': null,
  'palletInfo.logisticsCode': null,
  'palletInfo.managerName': null,
  'palletInfo.managerPhoneNumber': null,
  'palletInfo.zipCode': null,
  'palletInfo.address': null,
  'palletInfo.detailAddress': null,
  'palletInfo.managerEmail': null,
  'agreeInfo.isCheckedPacking': null,
  'agreeInfo.isCheckedDate': null,
};

const useStockReleaseRegisterValidation = () => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);

  const {
    requestGoods,
    requestGoodsState,
    detailInfo,
    detailInfoState,
    palletInfo,
    palletInfoState,
    agreeInfo,
    agreeInfoState,
  } = useStockReleaseStore(
    useShallow((state) => ({
      requestGoods: state.requestGoods,
      requestGoodsState: state.requestGoodsState,
      detailInfo: state.detailInfo,
      detailInfoState: state.detailInfoState,
      palletInfo: state.palletInfo,
      palletInfoState: state.palletInfoState,
      agreeInfo: state.agreeInfo,
      agreeInfoState: state.agreeInfoState,
    }))
  );
  const { setRequestGoodsStatus, setRegisterInfoState } = useStockReleaseStore(
    useShallow((state) => ({
      setRequestGoodsStatus: state.actions.setRequestGoodsStatus,
      setRegisterInfoState: state.actions.setRegisterInfoState,
    }))
  );

  const requestGoodsRef = useRef<HTMLDivElement | null>(null);
  const errorFieldRefs = useRef<TypeStockReleaseRegisterErrorFieldRefs>(DEFAULT_ERROR_FIELD_REFS);

  /** 반출 요청 상품 데이터 검증 */
  const validateRequestGoods = () => {
    // 반출 상품이 없는 경우
    if (requestGoods.length === 0) {
      return {
        isValid: false,
        message: '반출할 상품을 먼저 추가해주세요.',
      };
    }

    // 각 상품의 반출 수량 검증 및 상태 업데이트
    const newStatus = [...requestGoodsState];
    let hasError = false;

    requestGoods.forEach((item, index) => {
      if (!newStatus[index]) {
        newStatus[index] = {
          availableRequestQuantity: DEFAULT_INPUT_STATUS,
          defectiveRequestQuantity: DEFAULT_INPUT_STATUS,
        };
      }

      const { availableRequestQuantity = 0, defectiveRequestQuantity = 0 } = item;
      const totalQuantity = availableRequestQuantity + defectiveRequestQuantity;

      if (totalQuantity <= 0) {
        newStatus[index] = {
          availableRequestQuantity: ERROR_STATUS,
          defectiveRequestQuantity: ERROR_STATUS,
        };
        hasError = true;
      }
    });

    setRequestGoodsStatus(newStatus);

    if (hasError) {
      requestGoodsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      return {
        isValid: false,
        message: '반출 요청 수량 (가용) 혹은 반출 요청 수량 (불용) 중 하나는 0보다 커야 합니다.',
      };
    }

    return {
      isValid: true,
    };
  };

  /** 반출 요청 등록 데이터 검증 */
  const validationRegisterData = () => {
    const status: TypeRegisterInfoState = {
      detailInfoState: {
        ...detailInfoState,
        ...addIf(!detailInfo?.expectedDate, 'expectedDate', ERROR_REQUIRED_SELECT_STATUS),
        ...addIf(!detailInfo?.method, 'method', ERROR_REQUIRED_SELECT_STATUS),
        ...addIf(!detailInfo?.phoneNumber, 'phoneNumber', ERROR_REQUIRED_INPUT_STATUS),
        ...addIf(
          detailInfo?.phoneNumber && !validateTelFormat(detailInfo.phoneNumber),
          'phoneNumber',
          ERROR_INVALID_FORMAT_INPUT_STATUS
        ),
        ...addIf(!detailInfo?.zipCode, 'zipCode', ERROR_REQUIRED_INPUT_STATUS),
        ...addIf(!detailInfo?.address, 'address', ERROR_REQUIRED_INPUT_STATUS),
        ...addIf(
          (hasPermission3PL || STOCK_RELEASE_PACKING_METHOD_ALLOW_CODES.includes(detailInfo?.method ?? '')) &&
            !detailInfo?.packingMethod,
          'packingMethod',
          ERROR_REQUIRED_SELECT_STATUS
        ),
      },
      palletInfoState: {
        ...palletInfoState,
        ...(STOCK_RELEASE_PALLET_INFO_REQUIRED_CODES.includes(detailInfo?.packingMethod ?? '') && {
          ...addIf(!palletInfo?.palletType, 'palletType', ERROR_REQUIRED_SELECT_STATUS),
          ...addIf(!palletInfo?.logisticsCode, 'logisticsCode', ERROR_REQUIRED_INPUT_STATUS),
          ...addIf(!palletInfo?.managerName, 'managerName', ERROR_REQUIRED_INPUT_STATUS),
          ...addIf(!palletInfo?.managerPhoneNumber, 'managerPhoneNumber', ERROR_REQUIRED_INPUT_STATUS),
          ...addIf(
            palletInfo?.managerPhoneNumber && !validateTelFormat(palletInfo.managerPhoneNumber),
            'managerPhoneNumber',
            ERROR_INVALID_FORMAT_INPUT_STATUS
          ),
          ...addIf(!palletInfo?.managerEmail, 'managerEmail', ERROR_REQUIRED_INPUT_STATUS),
          ...addIf(
            palletInfo?.managerEmail && !validateEmailFormat(palletInfo.managerEmail),
            'managerEmail',
            ERROR_INVALID_FORMAT_INPUT_STATUS
          ),
          ...addIf(!palletInfo?.zipCode, 'zipCode', ERROR_REQUIRED_INPUT_STATUS),
          ...addIf(!palletInfo?.address, 'address', ERROR_REQUIRED_INPUT_STATUS),
        }),
      },
      agreeInfoState: {
        ...agreeInfoState,
        ...addIf(!agreeInfo?.isCheckedPacking, 'isCheckedPacking', ERROR_REQUIRED_SELECT_STATUS),
        ...addIf(!agreeInfo?.isCheckedDate, 'isCheckedDate', ERROR_REQUIRED_SELECT_STATUS),
      },
    };

    // 상태 업데이트
    setRegisterInfoState(status);

    // 에러가 있는지 확인
    const hasError =
      Object.values(status.detailInfoState).some((state) => state.status === 'error') ||
      Object.values(status.palletInfoState).some((state) => state.status === 'error') ||
      Object.values(status.agreeInfoState).some((state) => state.status === 'error');

    if (hasError) {
      const flattenedStates: Record<string, TypeInputState> = {
        ...Object.fromEntries(
          Object.entries(status.detailInfoState).map(([key, value]) => [`detailInfo.${key}`, value])
        ),
        ...Object.fromEntries(
          Object.entries(status.palletInfoState).map(([key, value]) => [`palletInfo.${key}`, value])
        ),
        ...Object.fromEntries(Object.entries(status.agreeInfoState).map(([key, value]) => [`agreeInfo.${key}`, value])),
      };
      focusFirstErrorField(errorFieldRefs.current, flattenedStates);
    }

    return {
      isValid: !hasError,
    };
  };

  return {
    requestGoodsRef,
    errorFieldRefs,
    validateRequestGoods,
    validationRegisterData,
  };
};

export default useStockReleaseRegisterValidation;
